<script setup lang="ts">
/**
 * The docked, non-blocking window of `AppModal` (`docked`; KIT_REQUIREMENTS §1 `AppModal`, wp/calendar-inbox §4.2.2 —
 * legacy `b-modal modal-sticky hide-backdrop no-fade static`, the inbox compose window). Not exported: hosts use
 * `<AppModal docked>`, which owns OK / discard and passes them here.
 *
 * Bottom-right, no backdrop and no focus trap: the page behind stays usable. The content stays **mounted** while the
 * window is minimised or hidden, so a draft survives both; only `AppModal`'s discard (or a successful OK) re-creates it
 * (`session`). Escape inside the window minimises it.
 */
import { computed, nextTick, ref, useId, watch } from 'vue'

import { useI18n } from '@/app/i18n'

import AppIcon from '../AppIcon.vue'

const props = defineProps<{
  title: string
  width: string
  /** Re-creates the slot content when it changes (discard, sent). */
  session: number
  closable: boolean
  busy: boolean
  hideFooter: boolean
}>()

const visible = defineModel<boolean>('visible', { required: true })
const minimized = defineModel<boolean>('minimized', { required: true })
const emit = defineEmits<{ discard: [] }>()
defineSlots<{ default?: () => unknown; footer?: () => unknown }>()

const { t } = useI18n()
const titleId = useId()
const root = ref<HTMLElement | null>(null)
const body = ref<HTMLElement | null>(null)
// Mounted on first show, then kept (hidden with v-show) so the draft survives `visible = false`.
const created = ref(visible.value)
const style = computed(() => ({ width: props.width }))

const FOCUSABLE = 'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]'

async function focusFirst(): Promise<void> {
  await nextTick()
  const target = body.value?.querySelector<HTMLElement>(`[autofocus], ${FOCUSABLE}`) ?? root.value
  target?.focus({ preventScroll: true })
}

watch(visible, (open) => {
  if (!open) return
  created.value = true
  minimized.value = false
  void focusFirst()
})
if (visible.value) void focusFirst()

function toggleMinimized(): void {
  minimized.value = !minimized.value
  if (!minimized.value) void focusFirst()
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.defaultPrevented || minimized.value) return
  event.preventDefault()
  minimized.value = true
  root.value?.focus({ preventScroll: true })
}
</script>

<template>
  <Teleport to="body">
    <section
      v-if="created"
      v-show="visible"
      ref="root"
      class="app-modal-docked"
      :class="{ 'is-minimized': minimized }"
      :style="style"
      :role="visible ? 'dialog' : undefined"
      aria-modal="false"
      :aria-labelledby="titleId"
      tabindex="-1"
      data-kit="modal"
      data-docked
      :data-minimized="minimized || undefined"
      @keydown="onKeydown"
    >
      <header class="app-modal-docked__header">
        <h5 :id="titleId" class="app-modal-docked__title" data-kit-title @click="minimized && toggleMinimized()">
          {{ title }}
        </h5>
        <div class="app-modal-docked__actions">
          <button
            type="button"
            class="app-modal-docked__action"
            :aria-label="minimized ? t('common.modal.restore') : t('common.modal.minimize')"
            :aria-expanded="!minimized"
            data-testid="modal-minimize"
            @click="toggleMinimized"
          >
            <AppIcon :name="minimized ? 'ChevronUpIcon' : 'MinusIcon'" :size="16" />
          </button>
          <button
            v-if="closable"
            type="button"
            class="app-modal-docked__action"
            :aria-label="t('common.modal.discard')"
            :disabled="busy"
            data-testid="modal-discard"
            @click="emit('discard')"
          >
            <AppIcon name="XIcon" :size="16" />
          </button>
        </div>
      </header>
      <div v-show="!minimized" ref="body" class="app-modal-docked__body">
        <div :key="session" class="app-modal-docked__content">
          <slot />
        </div>
      </div>
      <footer v-if="!hideFooter" v-show="!minimized" class="app-modal-docked__footer">
        <slot name="footer" />
      </footer>
    </section>
  </Teleport>
</template>

<style scoped>
/* Legacy `.modal-sticky`: fixed bottom-right at the content padding, shadow, no backdrop. Above the page, below the
   PrimeVue overlays it opens (selects: z-index from 1001) and the modals / confirms (1100+). */
.app-modal-docked {
  position: fixed;
  right: 2rem;
  bottom: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 1rem);
  max-height: calc(100vh - 2rem);
  color: var(--p-text-color);
  background: var(--p-content-background);
  border-radius: var(--p-border-radius-lg) var(--p-border-radius-lg) 0 0;
  box-shadow: 0 0 24px 0 rgb(0 0 0 / 25%);
}
.app-modal-docked:focus {
  outline: none;
}
.app-modal-docked:focus-visible {
  outline: 2px solid var(--p-primary-color);
}
.app-modal-docked.is-minimized {
  width: min(22rem, calc(100vw - 1rem)) !important;
}
.app-modal-docked__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 1.4rem;
  background: var(--p-surface-100);
  border-radius: var(--p-border-radius-lg) var(--p-border-radius-lg) 0 0;
}
.app-modal-docked__title {
  margin: 0;
  overflow: hidden;
  font-size: 1.07rem;
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.app-modal-docked.is-minimized .app-modal-docked__title {
  cursor: pointer;
}
.app-modal-docked__actions {
  display: flex;
  gap: 0.25rem;
}
.app-modal-docked__action {
  display: inline-flex;
  padding: 0.25rem;
  color: var(--p-text-color);
  background: transparent;
  border: 0;
  border-radius: 0.25rem;
  cursor: pointer;
}
.app-modal-docked__action:focus-visible {
  outline: 2px solid var(--p-primary-color);
}
.app-modal-docked__action:disabled {
  cursor: default;
  opacity: 0.5;
}
.app-modal-docked__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.app-modal-docked__content {
  padding: 1rem 1.4rem;
}
.app-modal-docked__footer {
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.8rem 1.4rem;
  border-top: 1px solid var(--p-content-border-color);
}
@media (max-width: 575.98px) {
  .app-modal-docked {
    right: 0.5rem;
    left: 0.5rem;
    width: auto !important;
  }
}
</style>
